import { useState, useEffect, useRef } from "react";
import { Sidebar as ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import { alpha } from "@mui/material/styles";
import { Avatar, Box, Chip, Typography, useTheme, IconButton } from "@mui/material";
import {
  HomeOutlined,
  PersonOutlined,
  PeopleOutlined,
  FamilyRestroom,
  ClassOutlined,
  MenuBook,
  Assignment,
  CalendarToday,
  CalendarMonth,
  AssignmentInd,
  TableChart,
  CalendarViewWeek,
  AccessTime,
  CampaignOutlined,
  Cancel,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { tokens } from "../theme";
import { getUserFromCookies } from "../utils/Cookie-utils";
import image from "../data/image";


const Item = ({ title, to, icon, selected, setSelected, closeSidebar }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const handleClick = () => {
    setSelected(title);
    closeSidebar();
  };

  return (
    <MenuItem
      active={selected === title}
      style={{ color: colors.grey[100] }}
      onClick={handleClick}
      icon={icon}
      component={<Link to={to} />}
    >
      <Typography>{title}</Typography>
    </MenuItem>
  );
};

const SidebarComponent = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [selected, setSelected] = useState("Dashboard");
  const user = getUserFromCookies();
  const role = user?.data?.role;
  const userName = user?.data?.name || "User";
  const sidebarRef = useRef(null);

  const closeSidebar = () => setIsSidebarOpen(false);

  const handleCloseSidebar = () => {
    closeSidebar();
  };

  // Handle click outside to close sidebar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        if (isSidebarOpen) {
          closeSidebar();
        }
      }
    };

    if (isSidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSidebarOpen]);

  if (!isSidebarOpen) return null;

  return (
    <Box
      ref={sidebarRef}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        height: "100vh",
        width: { xs: "100%", sm: "280px" },
        zIndex: 1400,
        overflowY: "auto",
        backgroundColor: colors.primary[400],
        borderRight: `1px solid ${alpha(colors.grey[100], 0.08)}`,
        boxShadow: "0 18px 40px rgba(0, 0, 0, 0.20)",
      }}
    >
      <ProSidebar
        backgroundColor="transparent"
        rootStyles={{
          "& .ps-sidebar-container": {
            backgroundColor: "transparent",
          },
          "& .ps-menu-button": {
            margin: "4px 12px",
            borderRadius: "12px",
            padding: "10px 16px",
            "&:hover": {
              color: colors.grey[100],
              backgroundColor: alpha(colors.blueAccent[400], 0.22),
            },
          },
          "& .ps-menu-button.ps-active": {
            color: colors.grey[100],
            backgroundColor: alpha(colors.blueAccent[500], 0.30),
          },
          "& .ps-menu-icon": {
            color: colors.grey[100],
          },
        }}
      >
        <Menu>
          <MenuItem
            style={{
              margin: "12px 0 20px 0",
              color: colors.grey[100],
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              px="12px"
            >
              <Typography variant="h4" fontWeight={700}>
                VISIGA-SIMS
              </Typography>
              <IconButton
                onClick={handleCloseSidebar}
                aria-label="Close sidebar"
                sx={{
                  color: colors.grey[100],
                }}
              >
                <Cancel />
              </IconButton>
            </Box>
          </MenuItem>

          <Box
            mx={2}
            mb={3}
            p={2}
            sx={{
              borderRadius: "16px",
              backgroundColor: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[600],
              border: `1px solid ${alpha(colors.grey[100], theme.palette.mode === "dark" ? 0.10 : 0.16)}`,
              boxShadow: theme.palette.mode === "dark"
                ? "0 10px 24px rgba(0, 0, 0, 0.18)"
                : "0 10px 24px rgba(20, 27, 45, 0.18)",
            }}
          >
            <Box display="flex" justifyContent="center" alignItems="center" mb={1.5}>
              <Avatar
                src={image.visigalogo3}
                alt="VISIGA logo"
                sx={{ width: 72, height: 72 }}
              />
            </Box>

            <Typography
              variant="body1"
              textAlign="center"
              fontWeight={700}
              color="#ffffff"
            >
              St Mary&apos;s Junior Seminary Visiga
            </Typography>
            <Typography
              variant="body2"
              textAlign="center"
              mt={0.5}
              color={theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[900]}
            >
              {userName}
            </Typography>
            {role && (
              <Box mt={1.5} display="flex" justifyContent="center">
                <Chip
                  size="small"
                  label={role}
                  sx={{
                    fontWeight: 700,
                    backgroundColor: theme.palette.mode === "dark" ? colors.blueAccent[500] : colors.blueAccent[400],
                    color: colors.grey[100],
                  }}
                />
              </Box>
            )}
          </Box>

          <Box pb={2}>
            {role === "ADMIN" && (
              <>
                <Item title="DASHBOARD" to="/dashboard" icon={<HomeOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="TEACHERS" to="/teachers" icon={<PersonOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="USERS" to="/users" icon={<PeopleOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="PARENTS" to="/parents" icon={<FamilyRestroom />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="CLASSES" to="/classes" icon={<ClassOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="SUBJECTS" to="/subjects" icon={<MenuBook />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="EXAMINATIONS" to="/examinations" icon={<Assignment />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="TERM" to="/terms" icon={<CalendarToday />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="DUTIES-WEEKS" to="/duties-weeks" icon={<CalendarMonth />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="TEACHERS-DUTY" to="/teachersDuty" icon={<AssignmentInd />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="CLASS-TIMETABLE" to="/classes-for-timetable" icon={<TableChart />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="SCHOOL-TIMETABLE" to="/schoolTimetable" icon={<CalendarViewWeek />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="CLASS-SESSION-TIME" to="/session-time-ranges" icon={<AccessTime />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="ANNOUNCEMENTS" to="/announcements" icon={<CampaignOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                
              </>
            )}
            {role === "TEACHER" && (
              <>
                <Item title="DASHBOARD" to="/dashboard" icon={<HomeOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
              </>
            )}
            {role === "PARENT" && (
              <>
                <Item title="DASHBOARD" to="/dashboard" icon={<HomeOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
              </>
            )}
            {role === "SECRETARY" && (
              <>
                <Item title="DASHBOARD" to="/dashboard" icon={<HomeOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="PARENTS" to="/parents" icon={<FamilyRestroom />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="CLASSES" to="/classes" icon={<ClassOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="ANNOUNCEMENTS" to="/announcements" icon={<CampaignOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
              </>
            )}
            {role === "ACCOUNTANT" && (
              <>
                <Item title="DASHBOARD" to="/dashboard" icon={<HomeOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="PARENTS" to="/parents" icon={<FamilyRestroom />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="CLASSES" to="/classes" icon={<ClassOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="ANNOUNCEMENTS" to="/announcements" icon={<CampaignOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
              </>
            )}
             {role === "CLASSTEACHER" && (
              <>
                <Item title="DASHBOARD" to="/dashboard" icon={<HomeOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="PARENTS" to="/parents" icon={<FamilyRestroom />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="CLASSES" to="/classes" icon={<ClassOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
              </>
            )}
            {role === "ACADEMIC"  && (
              <>
                <Item title="DASHBOARD" to="/dashboard" icon={<HomeOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="TEACHERS" to="/teachers" icon={<PersonOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="USERS" to="/users" icon={<PeopleOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="PARENTS" to="/parents" icon={<FamilyRestroom />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="CLASSES" to="/classes" icon={<ClassOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="SUBJECTS" to="/subjects" icon={<MenuBook />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="EXAMINATIONS" to="/examinations" icon={<Assignment />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="TERM" to="/terms" icon={<CalendarToday />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="DUTIES-WEEKS" to="/duties-weeks" icon={<CalendarMonth />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="CLASS-SESSION-TIME" to="/session-time-ranges" icon={<AccessTime />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                <Item title="ANNOUNCEMENTS" to="/announcements" icon={<CampaignOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeSidebar} />
                
              </>
            )}
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default SidebarComponent;